import React from 'react';
import { Plus, Trash2, AlertCircle, Wallet } from 'lucide-react';

/**
 * Inline split-payment configurator. Renders when the parent PO form has
 * Payment Terms = "Advance Payment".
 *
 * Props:
 *   value:    { enabled: bool, splits: [{percent, trigger, daysAfterGRN}, ...] }
 *   onChange: (nextValue) => void
 *
 * Validation (matches backend adv.validate_advance_config):
 *   - 1..10 splits
 *   - ≤ 1 row with trigger === 'Advance'
 *   - sum(percent) === 100
 *   - AfterGRN rows: 1 ≤ daysAfterGRN ≤ 90
 */
export default function AdvancePaymentSplitConfig({ value, onChange }) {
  const cfg = value || { enabled: false, splits: [] };
  const splits = Array.isArray(cfg.splits) ? cfg.splits : [];

  const setSplits = (next) => {
    onChange({ enabled: true, splits: next });
  };

  const updateSplit = (idx, patch) => {
    const next = splits.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setSplits(next);
  };

  const removeSplit = (idx) => {
    if (splits.length <= 1) return;
    setSplits(splits.filter((_, i) => i !== idx));
  };

  const setSplitCount = (n) => {
    n = Math.max(1, Math.min(10, parseInt(n) || 1));
    if (n === splits.length) return;
    // Auto-distribute evenly so user gets a valid starting config.
    // First row stays as 'Advance' if it already is (or becomes Advance if N == 1).
    const evenPct = Math.floor((100 / n) * 100) / 100;
    const remainder = Math.round((100 - evenPct * n) * 100) / 100;
    const next = Array.from({ length: n }, (_, i) => {
      const existing = splits[i];
      const isFirst = i === 0;
      const isLast = i === n - 1;
      return {
        percent: isLast ? (evenPct + remainder).toFixed(2) : evenPct.toFixed(2),
        trigger: existing?.trigger || (isFirst ? 'Advance' : 'AfterGRN'),
        daysAfterGRN: (existing?.trigger === 'Advance' || (isFirst && !existing))
          ? null
          : (existing?.daysAfterGRN ?? 7),
      };
    });
    setSplits(next);
  };

  // ----- Derived -----
  const sumPct = splits.reduce((acc, s) => acc + (parseFloat(s.percent) || 0), 0);
  const advanceCount = splits.filter(s => s.trigger === 'Advance').length;
  const sumValid = Math.abs(sumPct - 100) < 0.01;
  const advanceValid = advanceCount <= 1;
  const allValid = sumValid && advanceValid && splits.every(s => {
    const pct = parseFloat(s.percent);
    if (!(pct > 0)) return false;
    if (s.trigger === 'AfterGRN') {
      const d = parseInt(s.daysAfterGRN);
      return d >= 1 && d <= 90;
    }
    return true;
  });

  return (
    <div className="border border-amber-300 bg-amber-50/40 rounded-xl p-4 mt-3" data-testid="advance-payment-config">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-amber-700" />
          <h3 className="font-semibold text-amber-900 text-sm">Advance Payment — Split Configuration</h3>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-amber-900">Split into</label>
          <select
            value={splits.length || 1}
            onChange={(e) => setSplitCount(e.target.value)}
            className="px-2 py-1 border border-amber-300 rounded-md text-sm bg-white"
            data-testid="advance-split-count"
          >
            {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
              <option key={n} value={n}>{n} {n === 1 ? 'part' : 'parts'}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {splits.map((sp, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 flex-wrap bg-white border border-amber-200 rounded-lg px-3 py-2"
            data-testid={`advance-split-row-${idx}`}
          >
            <span className="text-xs font-semibold text-amber-700 w-14">Row {idx + 1}</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="100"
              value={sp.percent}
              onChange={(e) => updateSplit(idx, { percent: e.target.value })}
              placeholder="e.g. 30"
              className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm"
              data-testid={`advance-split-pct-${idx}`}
            />
            <span className="text-sm text-gray-700">%</span>

            {sp.trigger === 'AfterGRN' && (
              <>
                <span className="text-sm text-gray-600">after</span>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={sp.daysAfterGRN ?? ''}
                  onChange={(e) => updateSplit(idx, { daysAfterGRN: e.target.value === '' ? null : parseInt(e.target.value) })}
                  placeholder="7"
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                  data-testid={`advance-split-days-${idx}`}
                />
                <span className="text-sm text-gray-700">days</span>
              </>
            )}

            <span className="text-sm text-gray-600">on</span>

            <select
              value={sp.trigger}
              onChange={(e) => {
                const t = e.target.value;
                updateSplit(idx, {
                  trigger: t,
                  daysAfterGRN: t === 'Advance' ? null : (sp.daysAfterGRN ?? 7),
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
              data-testid={`advance-split-trigger-${idx}`}
            >
              <option value="Advance">Advance</option>
              <option value="AfterGRN">GRN</option>
            </select>

            {splits.length > 1 && (
              <button
                type="button"
                onClick={() => removeSplit(idx)}
                className="ml-auto text-rose-600 hover:bg-rose-50 p-1 rounded"
                title="Remove this row"
                data-testid={`advance-split-remove-${idx}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        {splits.length < 10 && (
          <button
            type="button"
            onClick={() => setSplitCount(splits.length + 1)}
            className="flex items-center gap-1 text-xs text-amber-700 hover:bg-amber-100 px-2 py-1 rounded"
            data-testid="advance-split-add"
          >
            <Plus size={12} /> Add another split
          </button>
        )}
      </div>

      {/* Validation summary */}
      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
        <div className={`flex items-center gap-1 ${sumValid ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="advance-split-sum">
          {!sumValid && <AlertCircle size={12} />}
          Sum: <b>{sumPct.toFixed(2)}%</b> {sumValid ? '✓' : '(must equal 100%)'}
        </div>
        {!advanceValid && (
          <div className="flex items-center gap-1 text-rose-700">
            <AlertCircle size={12} /> Only one Advance row allowed
          </div>
        )}
        {allValid && (
          <div className="text-emerald-700 text-xs">
            Ready — {splits.length} payment row{splits.length === 1 ? '' : 's'} will be created on save
          </div>
        )}
      </div>

      <p className="text-[11px] text-amber-800 mt-2 leading-relaxed">
        💡 Advance rows go straight to <b>Due</b> on save. Post-GRN rows stay hidden until all
        GRNs+QAs are done, then surface in <b>Upcoming</b> and move to <b>Due</b> after the day-count expires.
        Misc charges (if any) auto-add to the last row.
      </p>
    </div>
  );
}
